import React, { useState, useEffect, useRef } from 'react';
import { Box, Alert, Typography, Button, CircularProgress } from '@mui/material';
import { Download, OpenInNew } from '@mui/icons-material';
import mammoth from 'mammoth';

interface OfficeFileViewerProps {
  fileUrl: string;
  fileName: string;
  fileType: string;
  onError: () => void;
}

const OfficeFileViewer: React.FC<OfficeFileViewerProps> = ({
  fileUrl,
  fileName,
  fileType,
  onError
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [viewerError, setViewerError] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const renderOfficeFile = async () => {
      setIsLoading(true);
      setViewerError(false);

      try {
        if (fileType === 'docx') {
          // Use mammoth to convert DOCX to HTML (only supports DOCX, not DOC)
          const response = await fetch(fileUrl);
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const arrayBuffer = await response.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          
          if (result.value) {
            setHtmlContent(result.value);
          } else {
            throw new Error('No HTML content generated');
          }
          
          setIsLoading(false);
        } else {
          // For DOC files and other Office files, show download option
          setViewerError(true);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Office file rendering failed:', error);
        setViewerError(true);
        setIsLoading(false);
      }
    };

    renderOfficeFile();
  }, [fileUrl, fileType]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const handleOpenInNewTab = () => {
    window.open(fileUrl, '_blank');
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Đang tải file Office...
        </Typography>
      </Box>
    );
  }

  // If we have HTML content, render it
  if (htmlContent && !viewerError) {
    return (
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          p: 2,
          backgroundColor: 'white'
        }}
      >
        <Box
          ref={containerRef}
          dangerouslySetInnerHTML={{ __html: htmlContent }}
          sx={{
            '& *': {
              fontFamily: 'Arial, sans-serif',
              lineHeight: 1.6
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: '1em',
              marginBottom: '0.5em'
            },
            '& p': {
              marginBottom: '1em'
            },
            '& table': {
              borderCollapse: 'collapse',
              width: '100%',
              marginBottom: '1em'
            },
            '& th, & td': {
              border: '1px solid #ddd',
              padding: '8px',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: '#f2f2f2'
            }
          }}
        />
      </Box>
    );
  }

  if (viewerError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 4,
          textAlign: 'center'
        }}
      >
        <Alert severity="info" sx={{ mb: 3, maxWidth: 500 }}>
          {fileType === 'doc' 
            ? 'File DOC (Word 97-2003) không thể preview trực tiếp. Vui lòng tải xuống để xem.'
            : `File ${fileType.toUpperCase()} không thể preview trực tiếp. Vui lòng tải xuống để xem.`
          }
        </Alert>
        
        <Typography variant="h6" gutterBottom>
          {fileName}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Định dạng: {fileType.toUpperCase()}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            sx={{ minWidth: 120 }}
          >
            Tải xuống
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={handleOpenInNewTab}
            sx={{ minWidth: 120 }}
          >
            Mở trong tab mới
          </Button>
        </Box>
      </Box>
    );
  }

  // This should not be reached due to the conditions above
  return null;
};

export default OfficeFileViewer;
